import { useEffect, useState } from 'react'
import { Camera, CameraOff, MonitorSmartphone, RefreshCw, SwitchCamera, Unlink, Volume2 } from 'lucide-react'
import { foutTekst, vraag, zetApparaat } from '../lib/api'
import { Lade } from '../onderdelen/Lade'
import { PinPad } from '../onderdelen/PinPad'
import { speel, STIJLEN, stijl, zetStijl, type Stijl } from './geluid'
import { MENU_KNOPPEN, type MenuKnop } from './Thuis'

/*
 * Het medewerkersmenu op de tablet: lang drukken op het logo (of vijf keer
 * snel op de klok), dan de PIN van iemand die op deze vestiging werkt.
 * Hierachter zitten de dingen die een klant niet hoort te kunnen: de camera
 * omzetten, de tablet ontkoppelen. En wat er vandaag is ingewisseld.
 */

interface MenuData {
  persoon: string; magOntkoppelen: boolean
  vandaag: { wasbeurten: number; nieuweKaarten: number; inwisselingen: number }
  inwisselingen: { t: string; beloning: string; punten: number; medewerker: string; tag: string }[]
}

export function Medewerkersmenu({ slug, lengte, open, sluit, cameraAan, zetCameraAan, wisselCamera, schermAan, knoppen, zetKnoppen }: {
  slug: string; lengte: number; open: boolean; sluit: () => void
  cameraAan: boolean; zetCameraAan: (aan: boolean) => void; wisselCamera: () => void; schermAan: boolean
  knoppen: MenuKnop[]; zetKnoppen: (k: MenuKnop[]) => void
}) {
  const [pin, zetPin] = useState<string | null>(null)
  const [data, zetData] = useState<MenuData | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  const [bezig, zetBezig] = useState(false)
  const [geluid, zetGeluid] = useState<Stijl>(stijl)

  /* Dicht = alles vergeten, ook de PIN. Na twee minuten vanzelf dicht. */
  useEffect(() => {
    if (!open) { zetPin(null); zetData(null); zetFout(null); return }
    const t = setTimeout(sluit, 120_000)
    return () => clearTimeout(t)
  }, [open, sluit])

  const probeer = async (p: string) => {
    zetBezig(true); zetFout(null)
    try {
      const r = await vraag<MenuData & { status: string; over?: number }>('tablet-menu', { slug, pin: p })
      if (r.status === 'pin-fout') { zetFout(`Onjuiste PIN. Nog ${r.over} ${r.over === 1 ? 'poging' : 'pogingen'}.`); return }
      if (r.status === 'geblokkeerd') { zetFout('De PIN is geblokkeerd na te veel foute pogingen. Een manager geeft hem vrij in Beheer.'); return }
      zetPin(p); zetData(r)
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }

  const ontkoppel = async () => {
    if (!pin || !confirm('Deze tablet ontkoppelen? Hij kan daarna niet meer scannen tot hij opnieuw gekoppeld is.')) return
    try {
      await vraag('tablet-ontkoppelen', { slug, pin })
      zetApparaat(null)
      location.reload()
    } catch (e) { zetFout(foutTekst(e)) }
  }

  const tijd = (iso: string) => new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })

  return (
    <Lade titel={data ? `Medewerker: ${data.persoon}` : 'Medewerkersmenu'} open={open} sluit={sluit} klasse={`kiosk-lade ${data ? '' : 'pin-lade'}`} breed={!!data} kinderen={
      !data ? (
        <div>
          <p style={{ textAlign: 'center', color: '#a3b2ce', fontSize: 17 }}>Vul je PIN in</p>
          <PinPad lengte={lengte} bezig={bezig} fout={fout} klaar={probeer} />
          {fout && <div className="melding fout" style={{ marginTop: 14 }}>{fout}</div>}
        </div>
      ) : (
        <div className="lijst" style={{ gap: 16 }}>
          <div className="tegels" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 0 }}>
            <div className="kaart tegel"><small>Wasbeurten</small><b>{data.vandaag.wasbeurten}</b><span>vandaag</span></div>
            <div className="kaart tegel"><small>Ingewisseld</small><b>{data.vandaag.inwisselingen}</b><span>vandaag</span></div>
            <div className="kaart tegel"><small>Nieuw</small><b>{data.vandaag.nieuweKaarten}</b><span>vandaag</span></div>
          </div>

          <div className="route-knoppen">
            <button className="knop" onClick={() => zetCameraAan(!cameraAan)}>{cameraAan ? <><CameraOff size={20} />Camera uit</> : <><Camera size={20} />Camera aan</>}</button>
            <button className="knop" onClick={wisselCamera} disabled={!cameraAan}><SwitchCamera size={20} />Andere camera</button>
            <button className="knop" onClick={() => location.reload()}><RefreshCw size={20} />Vernieuwen</button>
            {data.magOntkoppelen && <button className="knop gevaar" onClick={ontkoppel}><Unlink size={20} />Tablet ontkoppelen</button>}
          </div>
          <p className="stil klein" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <MonitorSmartphone size={16} />{schermAan ? 'Het scherm blijft aan.' : 'Deze browser kan het scherm niet zelf aan houden; zet automatisch vergrendelen uit in de instellingen van de tablet.'}
          </p>
          {fout && <div className="melding fout">{fout}</div>}

          <section>
            <div className="sectie-kop"><h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Volume2 size={20} />Geluid bij scannen</h2></div>
            <div className="chips" style={{ marginBottom: 6 }}>
              {STIJLEN.map((g) => (
                <button key={g.k} className="chip" aria-pressed={geluid === g.k} onClick={() => { zetGeluid(g.k); zetStijl(g.k); speel('punt', g.k) }}>{g.naam}</button>
              ))}
            </div>
            <p className="stil klein">Tik om te horen. Bij punten erbij klinkt de gekozen stijl. Al gekregen, een fout en een inwisseling hebben hun eigen vaste geluid.</p>
            {geluid !== 'uit' && (
              <div className="chips" style={{ marginTop: 8, marginBottom: 0 }}>
                <button className="chip" onClick={() => speel('wacht', geluid)}>Al gekregen</button>
                <button className="chip" onClick={() => speel('fout', geluid)}>Fout</button>
                <button className="chip" onClick={() => speel('ingewisseld', geluid)}>Ingewisseld</button>
              </div>
            )}
          </section>

          <section>
            <div className="sectie-kop"><h2>Menu aan de zijkant</h2></div>
            <p className="stil klein" style={{ marginBottom: 8 }}>Wat de klant ziet onder de menuknop linksonder op het beginscherm. Instellingen staat er altijd in, en gaat alleen open met een PIN.</p>
            <div className="chips" style={{ marginBottom: 0 }}>
              {MENU_KNOPPEN.map((m) => {
                const aan = knoppen.includes(m.k)
                return (
                  <button key={m.k} className="chip" aria-pressed={aan}
                    onClick={() => zetKnoppen(aan ? knoppen.filter((x) => x !== m.k) : [...knoppen, m.k])}>{aan ? '✓ ' : ''}{m.naam}</button>
                )
              })}
            </div>
          </section>

          <section>
            <div className="sectie-kop"><h2>Vandaag ingewisseld</h2></div>
            {data.inwisselingen.length ? (
              <ul className="tijdlijn">
                {data.inwisselingen.map((r, i) => (
                  <li key={i}>
                    <b className="cijfer" style={{ minWidth: 52 }}>{tijd(r.t)}</b>
                    <span className="vul">{r.beloning} <span className="stil">· {r.punten} pt · {r.medewerker || '?'}</span></span>
                    <span className="code-pil stil">{r.tag}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="stil">Nog niets vandaag.</p>}
          </section>
        </div>
      )
    } />
  )
}
